import { Icon, TextInput } from '@tremor/react';
import { RiSearchLine } from '@remixicon/react';
import { EyeIcon, FunnelIcon } from '@heroicons/react/24/solid';
import {
  CheckIcon,
  ChevronLeftIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Menu, Popover, Position } from 'evergreen-ui';
import { Checkbox } from '../../../ui/component/checkbox';
import { ReactNode } from 'react';

export type TimesheetStatus = 'pending' | 'approved' | 'declined';

export type TimesheetFilters = {
  search: string;
  employees: string[];
  statuses: TimesheetStatus[];
  from: string;
  to: string;
};

export const EMPTY_TIMESHEET_FILTERS: TimesheetFilters = {
  search: '',
  employees: [],
  statuses: [],
  from: '',
  to: '',
};

// The statuses the API stores (`details.approval_status`).
export const TIMESHEET_STATUSES: { id: TimesheetStatus; label: string }[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'declined', label: 'Declined' },
];

/** The rows that match `filters`. Dates are compared as YYYY-MM-DD strings. */
export function filterTimesheets(rows: any[], filters: TimesheetFilters) {
  const search = filters.search.trim().toLowerCase();
  return (rows || []).filter((row) => {
    if (
      filters.employees.length > 0 &&
      !filters.employees.includes(String(row?.employee ?? row?.user?.id ?? ''))
    ) {
      return false;
    }
    if (
      filters.statuses.length > 0 &&
      !filters.statuses.includes(row?.approval_status)
    ) {
      return false;
    }
    const date = String(row?.date ?? '').slice(0, 10);
    if (filters.from && date < filters.from) return false;
    if (filters.to && date > filters.to) return false;
    if (search) {
      const haystack = [row?.user?.name, row?.user?.email, row?.notes, date]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
}

type Props = {
  timesheetData: any[];
  handleDeclineModal: (id: string) => void;
  handleApprove: (item: any) => void;
  handleTimesheetsDetails: (id: string) => void;
  filters: TimesheetFilters;
  setFilters: (filters: TimesheetFilters) => void;
  /** Shown (with its filter) only to managers and admins. */
  people?: { id: string; name: string }[];
  canFilterByEmployee?: boolean;
  actions?: ReactNode;
};

export function TimesheetsTable({
  timesheetData,
  handleDeclineModal,
  handleApprove,
  handleTimesheetsDetails,
  filters,
  setFilters,
  people = [],
  canFilterByEmployee = false,
  actions,
}: Props) {
  const rows = filterTimesheets(timesheetData, filters);
  const update = (patch: Partial<TimesheetFilters>) =>
    setFilters({ ...filters, ...patch });
  const stop = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <div className="mt-2 rounded-xl border-[1px] border-gray-100 px-6 py-2 shadow">
      <div className="flex flex-row justify-end gap-4">
        <TextInput
          icon={RiSearchLine}
          placeholder="Search"
          className="w-auto lg:w-[200px]"
          value={filters.search}
          onChange={(event) => update({ search: event.target.value })}
        />
        <Popover
          position={Position.LEFT}
          content={
            <Menu>
              <Menu.Group>
                {canFilterByEmployee && (
                  <Popover
                    position={Position.LEFT}
                    content={
                      <Menu>
                        <Menu.Group>
                          <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
                            {people.length === 0 && (
                              <p className="px-3 py-1 text-sm text-gray-500">
                                No people found
                              </p>
                            )}
                            {people.map((employee) => (
                              <Menu.Item key={employee.id} onClick={stop}>
                                <div className="flex flex-row items-center gap-2">
                                  <Checkbox
                                    className="rounded-md border-[1px]"
                                    color="rose"
                                    checked={filters.employees.includes(
                                      employee.id,
                                    )}
                                    onChange={() =>
                                      update({
                                        employees: toggle(
                                          filters.employees,
                                          employee.id,
                                        ),
                                      })
                                    }
                                  />
                                  {employee.name}
                                </div>
                              </Menu.Item>
                            ))}
                          </div>
                          <div className="mt-1 flex flex-row justify-center gap-2">
                            <button
                              className=" w-[80%] rounded-lg border-[1px] py-1 shadow"
                              onClick={() => update({ employees: [] })}
                            >
                              Clear all
                            </button>
                          </div>
                        </Menu.Group>
                      </Menu>
                    }
                  >
                    <Menu.Item>
                      <div className="flex flex-row flex-nowrap gap-2">
                        <ChevronLeftIcon className="w-4" />
                        Employee
                      </div>
                    </Menu.Item>
                  </Popover>
                )}

                <Popover
                  position={Position.LEFT}
                  content={
                    <div className="flex flex-col gap-2 p-3" onClick={stop}>
                      <label className="flex flex-col text-sm text-gray-600">
                        From
                        <input
                          type="date"
                          aria-label="From date"
                          className="rounded-md border border-gray-300 px-2 py-1"
                          value={filters.from}
                          max={filters.to || undefined}
                          onChange={(event) =>
                            update({ from: event.target.value })
                          }
                        />
                      </label>
                      <label className="flex flex-col text-sm text-gray-600">
                        To
                        <input
                          type="date"
                          aria-label="To date"
                          className="rounded-md border border-gray-300 px-2 py-1"
                          value={filters.to}
                          min={filters.from || undefined}
                          onChange={(event) =>
                            update({ to: event.target.value })
                          }
                        />
                      </label>
                      <button
                        className="w-full rounded-lg border-[1px] py-1 shadow"
                        onClick={() => update({ from: '', to: '' })}
                      >
                        Clear dates
                      </button>
                    </div>
                  }
                >
                  <Menu.Item>
                    <div className="flex flex-row flex-nowrap gap-2">
                      <ChevronLeftIcon className="w-4" />
                      Dates
                    </div>
                  </Menu.Item>
                </Popover>

                <Popover
                  position={Position.LEFT}
                  content={
                    <Menu>
                      <Menu.Group>
                        <div className="flex flex-col gap-1">
                          {TIMESHEET_STATUSES.map((status) => (
                            <Menu.Item key={status.id} onClick={stop}>
                              <div className="flex flex-row items-center gap-2">
                                <Checkbox
                                  className="rounded-md border-[1px]"
                                  color="rose"
                                  checked={filters.statuses.includes(status.id)}
                                  onChange={() =>
                                    update({
                                      statuses: toggle(
                                        filters.statuses,
                                        status.id,
                                      ),
                                    })
                                  }
                                />
                                {status.label}
                              </div>
                            </Menu.Item>
                          ))}
                        </div>
                        <div className="mt-1 flex flex-row justify-center gap-2">
                          <button
                            className=" w-[80%] rounded-lg border-[1px] py-1 shadow"
                            onClick={() => update({ statuses: [] })}
                          >
                            Clear all
                          </button>
                        </div>
                      </Menu.Group>
                    </Menu>
                  }
                >
                  <Menu.Item>
                    <div className="flex flex-row flex-nowrap gap-2">
                      <ChevronLeftIcon className="w-4" />
                      Status
                    </div>
                  </Menu.Item>
                </Popover>
              </Menu.Group>
            </Menu>
          }
        >
          <button
            className="flex items-center 
            rounded-lg border-[1px] px-3 font-normal shadow"
          >
            <Icon icon={FunnelIcon} size="sm" color="blue" />
            Filter
          </button>
        </Popover>
        {actions}
      </div>
      <div className="overflow-hidden">
        <div className=" flow-root w-full">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full divide-y divide-gray-300">
                <thead>
                  <th
                    scope="col"
                    className=" flex items-center gap-3 py-3.5 pl-4 text-left text-sm font-medium text-gray-500 sm:pl-0"
                  >
                    Employee
                  </th>
                  <th
                    scope="col"
                    className="px-2 py-3.5 text-left text-sm font-medium text-gray-500"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    className="px-2 py-3.5 text-left text-sm font-medium text-gray-500"
                  >
                    Work time
                  </th>
                  <th
                    scope="col"
                    className="px-2 py-3.5 text-left text-sm font-medium text-gray-500"
                  >
                    Hours worked
                  </th>
                  <th
                    scope="col"
                    className="px-2 py-3.5 text-left text-sm font-medium text-gray-500"
                  >
                    Approval status
                  </th>
                  <th
                    scope="col"
                    className="px-2 py-3.5 text-left text-sm font-medium text-gray-500"
                  ></th>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-4 text-center text-sm text-gray-500"
                      >
                        No timesheets match these filters
                      </td>
                    </tr>
                  )}
                  {rows.map((item: any) => (
                      <tr key={item.id ?? item._id}>
                        <td className="whitespace-nowrap py-2 pl-4 text-sm text-gray-900 sm:pl-0">
                          <div className="flex items-center gap-2">
                            <UserIcon className="w-5" />
                            <div className="flex flex-col ">
                              <p>{item?.user?.name}</p>
                              <p className="text-sm text-gray-500">
                                {item?.user?.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-sm ">
                          {item?.date}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-sm ">
                          {item?.work_time?.from} - {item?.work_time?.to}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-500">
                          {item?.hours_worked}h
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-sm ">
                          {item?.approval_status === 'pending' ? (
                            <div className="flex max-w-[90px] justify-center rounded-lg bg-[#FFEDD5] px-2 py-1 text-[#C2410C]">
                              Pending
                            </div>
                          ) : item?.approval_status === 'approved' ? (
                            <p className="flex max-w-[90px] justify-center rounded-lg bg-[#DCFCE7] px-2 py-1 text-[#15803D]">
                              Approved
                            </p>
                          ) : (
                            <div className="flex max-w-[90px] justify-center rounded-lg bg-[#FEE2E2] px-2 py-1 text-[#DC2626]">
                              Declined
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-500">
                          {item?.approval_status === 'approved' ? (
                            <p>{item?.approved_time}</p>
                          ) : (
                            <div className="flex flex-row items-center gap-5">
                              <button
                                onClick={() => handleDeclineModal(item?.id)}
                                className="rounded-full bg-[#DC2626] p-1"
                              >
                                <XMarkIcon className="w-3" color="white" />
                              </button>
                              <button
                                onClick={() => handleApprove(item)}
                                className="rounded-full bg-[#15803D] p-1"
                              >
                                <CheckIcon className="w-3" color="white" />
                              </button>
                              <button
                                onClick={() => handleTimesheetsDetails(item?.id)}
                                className="rounded-full"
                              >
                                <EyeIcon className="w-5" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
